import React, { useState, useEffect } from 'react';
import { useBodyScrollLock } from '../utils/scrollLock';
import {
  Anime,
  AnimeSeason,
  AnimeSource,
  BroadcastDay,
  BROADCAST_DAYS,
  SEASON_CONFIGS,
  TierGrade,
  TIER_CONFIGS,
  WakuFeedItem,
} from '../types/anime';
import {
  fetchWakuDetail,
  JikanAnimeItem,
  mapJikanBroadcastDay,
  mapJikanSeason,
  searchJikanAnime,
  searchWakuAnime,
  searchBilibiliAnime,
} from '../services/animeService';
import {
  detectPlatformFromUrl,
  formatImageUrl,
  getCurrentSeasonAndYear,
  pruneEpisodesToSources,
} from '../utils/helpers';
import {
  X,
  Plus,
  Trash2,
  Link as LinkIcon,
  Image as ImageIcon,
  Upload,
  Search,
  Bell,
  Calendar,
  Clock,
  Sparkles,
  Loader2,
} from 'lucide-react';

interface AnimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (anime: Anime) => void;
  onDelete?: (id: string) => void;
  initialData?: Anime | null;
}

export const AnimeModal: React.FC<AnimeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialData,
}) => {
  const defaultSeasonYear = getCurrentSeasonAndYear();

  const [title, setTitle] = useState('');
  const [thaiTitle, setThaiTitle] = useState('');
  const [grade, setGrade] = useState<TierGrade>('EX');
  const [season, setSeason] = useState<AnimeSeason>(defaultSeasonYear.season);
  const [year, setYear] = useState<number>(defaultSeasonYear.year);
  const [status, setStatus] = useState<'กำลังฉาย' | 'จบแล้ว' | 'รอฉาย'>('กำลังฉาย');
  const [audioType, setAudioType] = useState<'ซับไทย' | 'พากย์ไทย' | 'ซับ+พากย์'>('ซับไทย');
  const [broadcastDay, setBroadcastDay] = useState<BroadcastDay>('UNKNOWN');
  const [broadcastTime, setBroadcastTime] = useState('');
  const [notifyLine, setNotifyLine] = useState(true);
  const [watchedEp, setWatchedEp] = useState(0);
  const [latestEp, setLatestEp] = useState(1);
  const [totalEp, setTotalEp] = useState(12);
  const [score, setScore] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [sources, setSources] = useState<AnimeSource[]>([]);

  // Auto-discovery states
  const [isSearchingAuto, setIsSearchingAuto] = useState(false);
  const [wakuCandidates, setWakuCandidates] = useState<WakuFeedItem[]>([]);
  const [jikanCandidates, setJikanCandidates] = useState<JikanAnimeItem[]>([]);
  const [autoLog, setAutoLog] = useState<string | null>(null);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setThaiTitle(initialData.thaiTitle || '');
      setGrade(initialData.grade || 'A');
      setSeason(initialData.season || defaultSeasonYear.season);
      setYear(initialData.year || defaultSeasonYear.year);
      setStatus(initialData.status || 'กำลังฉาย');
      setAudioType(initialData.audioType || 'ซับไทย');
      setBroadcastDay(initialData.broadcastDay || 'UNKNOWN');
      setBroadcastTime(initialData.broadcastTime || '');
      setNotifyLine(initialData.notifyLine ?? true);
      setWatchedEp(Number(initialData.watchedEp) || 0);
      setLatestEp(Number(initialData.latestEp) || 0);
      setTotalEp(Number(initialData.totalEp) || 0);
      setScore(initialData.score || '');
      setImageUrl(initialData.imageUrl || '');
      setSources(
        initialData.sources && initialData.sources.length > 0
          ? initialData.sources
          : [{ type: 'anime-waku', name: 'Anime-Waku', url: '', latestEp: 0 }]
      );
      setWakuCandidates([]);
      setJikanCandidates([]);
      setAutoLog(null);
    } else {
      setTitle('');
      setThaiTitle('');
      setGrade('EX');
      setSeason(defaultSeasonYear.season);
      setYear(defaultSeasonYear.year);
      setStatus('กำลังฉาย');
      setAudioType('ซับไทย');
      setBroadcastDay('UNKNOWN');
      setBroadcastTime('');
      setNotifyLine(true);
      setWatchedEp(0);
      setLatestEp(1);
      setTotalEp(12);
      setScore('');
      setImageUrl('');
      setSources([{ type: 'anime-waku', name: 'Anime-Waku', url: '', latestEp: 0 }]);
      setWakuCandidates([]);
      setJikanCandidates([]);
      setAutoLog(null);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleAutoSearch = async () => {
    const q = title.trim() || thaiTitle.trim();
    if (!q) {
      alert('กรุณาพิมพ์ชื่อเรื่องอนิเมะ (ภาษาอังกฤษ โรมาจิ หรือภาษาไทย) ก่อนกดค้นหาอัตโนมัติ');
      return;
    }

    setIsSearchingAuto(true);
    setAutoLog('กำลังค้นหาจาก Bilibili, Anime-Waku และฐานข้อมูล MyAnimeList...');
    setWakuCandidates([]);
    setJikanCandidates([]);

    try {
      const [wakuRes, biliRes, jikanRes] = await Promise.all([
        searchWakuAnime(q),
        searchBilibiliAnime(q),
        searchJikanAnime(q),
      ]);
      const combinedFeed = [...biliRes, ...wakuRes];
      setWakuCandidates(combinedFeed);
      setJikanCandidates(jikanRes);

      if (combinedFeed.length === 0 && jikanRes.length === 0) {
        setAutoLog('ไม่พบผลลัพธ์อัตโนมัติ ลองใช้ชื่อภาษาไทย/อังกฤษสั้นๆ หรือวาง URL จากเว็บโดยตรง');
      } else {
        setAutoLog(
          `พบผลลัพธ์จากเว็บดูอนิเมะ ${combinedFeed.length} เรื่อง (Bilibili / Anime-Waku) และ MyAnimeList ${jikanRes.length} เรื่อง`
        );
      }
    } catch (e: any) {
      setAutoLog(`เกิดข้อผิดพลาด: ${e.message}`);
    } finally {
      setIsSearchingAuto(false);
    }
  };

  const handleApplyWakuCandidate = async (cand: WakuFeedItem) => {
    setTitle(cand.romajiTitle || cand.displayTitle);
    if (cand.thaiTitle) setThaiTitle(cand.thaiTitle);
    if (cand.imageUrl) setImageUrl(cand.imageUrl);
    if (cand.latestEp > 0) setLatestEp(cand.latestEp);
    if (cand.totalEp && cand.totalEp > 0) setTotalEp(cand.totalEp);
    setAudioType(cand.audioType);
    setStatus(cand.status);
    setSeason(cand.season);
    setYear(cand.year);
    if (cand.rating) setScore(cand.rating);
    if (cand.broadcastDay && cand.broadcastDay !== 'UNKNOWN') {
      setBroadcastDay(cand.broadcastDay);
    }
    if (cand.broadcastTime) {
      setBroadcastTime(cand.broadcastTime);
    }

    const pType = cand.platform === 'bilibili' ? 'bilibili' : 'anime-waku';
    const pName = pType === 'bilibili' ? 'Bilibili' : 'Anime-Waku';

    const existingNonEmpty = sources.filter((s) => s.url.trim() !== '');
    const hasUrl = existingNonEmpty.some((s) => s.url === cand.url);
    if (!hasUrl) {
      setSources([
        {
          type: pType,
          name: pName,
          url: cand.url,
          latestEp: cand.latestEp,
        },
        ...existingNonEmpty,
      ]);
    }
    setAutoLog(`✓ ดึงข้อมูลจาก ${pName} สำเร็จ! (${cand.displayTitle})`);

    // Also fetch detail for total episodes & exact episode count & schedule
    const detail = await fetchWakuDetail(cand.url);
    if (detail) {
      if (detail.latestEp > cand.latestEp) setLatestEp(detail.latestEp);
      if (detail.totalEpisodes > 0) setTotalEp(detail.totalEpisodes);
      if (detail.broadcastDay && detail.broadcastDay !== 'UNKNOWN') {
        setBroadcastDay(detail.broadcastDay);
      }
      if (detail.broadcastTime) {
        setBroadcastTime(detail.broadcastTime);
      }
    }
  };

  const handleApplyJikanCandidate = (item: JikanAnimeItem) => {
    setTitle(item.title || item.title_english || '');
    const img =
      item.images?.webp?.large_image_url || item.images?.jpg?.large_image_url || '';
    if (img && !imageUrl) setImageUrl(img);
    if (item.episodes) setTotalEp(item.episodes);
    if (item.score) setScore(String(item.score));

    const mappedSeason = mapJikanSeason(item.season);
    if (mappedSeason) setSeason(mappedSeason);
    if (item.year) setYear(item.year);

    const mappedDay = mapJikanBroadcastDay(item.broadcast?.day);
    if (mappedDay !== 'UNKNOWN') setBroadcastDay(mappedDay);
    if (item.broadcast?.time) setBroadcastTime(item.broadcast.time);

    setAutoLog(
      `✓ ดึงตารางวันฉายและฤดูกาลจาก MyAnimeList สำเร็จ! (${
        item.broadcast?.day || 'ไม่ระบุวัน'
      } ${item.broadcast?.time || ''})`
    );
  };

  const handleAddSource = (presetName?: string, presetUrl?: string) => {
    const info = presetUrl
      ? detectPlatformFromUrl(presetUrl)
      : { type: 'other' as const, name: presetName || '' };
    setSources([
      ...sources,
      {
        type: info.type,
        name: presetName || info.name,
        url: presetUrl || '',
        latestEp: latestEp || 0,
      },
    ]);
  };

  const handleSourceChange = (idx: number, field: 'name' | 'url', val: string) => {
    const next = [...sources];
    if (field === 'url') {
      next[idx].url = val;
      const detected = detectPlatformFromUrl(val);
      next[idx].type = detected.type;
      if (!next[idx].name || next[idx].name === 'เว็บไซต์หลัก') {
        next[idx].name = detected.name;
      }
    } else {
      next[idx].name = val;
    }
    setSources(next);
  };

  const handleRemoveSource = (idx: number) => {
    setSources(sources.filter((_, i) => i !== idx));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageUrl(String(reader.result || ''));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanedSources = sources
      .filter((s) => s.url.trim() !== '')
      .map((s) => {
        const det = detectPlatformFromUrl(s.url);
        return {
          type: det.type,
          name: s.name.trim() || det.name,
          url: s.url.trim(),
          latestEp: Math.max(Number(s.latestEp) || 0, Number(latestEp) || 0),
        };
      });

    const animeData: Anime = {
      id: initialData?.id || `ANIME_${Date.now()}`,
      title: title.trim() || thaiTitle.trim(),
      thaiTitle: thaiTitle.trim(),
      grade,
      season,
      year: Number(year) || defaultSeasonYear.year,
      watchedEp: Number(watchedEp) || 0,
      latestEp: Number(latestEp) || 0,
      totalEp: Number(totalEp) || 0,
      imageUrl: imageUrl.trim(),
      status,
      audioType,
      broadcastDay,
      broadcastTime: broadcastTime.trim(),
      notifyLine,
      score: score.trim(),
      malTitle: initialData?.malTitle,
      order: initialData?.order ?? 9999,
      sources: cleanedSources,
      episodes: initialData?.episodes,
      synopsis: initialData?.synopsis,
      lastUpdatedAt: initialData?.lastUpdatedAt,
      detectedDayNote: initialData?.detectedDayNote,
    };

    // A removed website takes its episodes (and the episode count they set) with it
    onSave(pruneEpisodesToSources(animeData));
    onClose();
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2.5 sm:p-4 safe-area-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-700/90 rounded-2xl max-w-2xl w-full max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh] flex flex-col shadow-2xl overflow-hidden"
      >
        {/* Modal Header */}
        <div className="bg-slate-800/90 px-5 py-3.5 border-b border-slate-700/80 flex items-center justify-between shrink-0">
          <h3 className="text-base sm:text-lg font-extrabold text-purple-300 flex items-center gap-2">
            <span>🎬</span>
            <span>{initialData ? 'แก้ไขข้อมูลอนิเมะ' : 'เพิ่มอนิเมะเรื่องใหม่'}</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-700/50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form
          id="anime-edit-form"
          onSubmit={handleSubmit}
          className="p-4 sm:p-6 space-y-4 flex-1 overflow-y-auto overscroll-contain"
        >
          {/* 1. Title & Thai Title + Auto-Search */}
          <div className="space-y-3 bg-slate-950/60 p-3.5 rounded-xl border border-slate-800">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  ชื่อเรื่องอนิเมะ (อังกฤษ / โรมาจิ) <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="เช่น Kusuriya no Hitorigoto Season 3"
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  ชื่อภาษาไทย (ถ้ามี)
                </label>
                <input
                  type="text"
                  value={thaiTitle}
                  onChange={(e) => setThaiTitle(e.target.value)}
                  placeholder="เช่น สืบคดีปริศนา หมอยาตำรับโคมแดง"
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={handleAutoSearch}
                disabled={isSearchingAuto}
                className="px-3.5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow"
              >
                {isSearchingAuto ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Search className="w-3.5 h-3.5" />
                )}
                <span>
                  {isSearchingAuto
                    ? 'กำลังค้นหาจาก Bilibili / Anime-Waku / MAL...'
                    : '✨ ค้นหาข้อมูล/รูปปก/ตารางฉายอัตโนมัติ (Bilibili + Anime-Waku + MAL)'}
                </span>
              </button>
              <span className="text-[11px] text-slate-400">
                พิมพ์ชื่อเรื่องแล้วกดปุ่มเพื่อดึงรูปปก ตอนล่าสุด และวันฉายอัตโนมัติ
              </span>
            </div>

            {autoLog && (
              <div className="text-xs text-teal-300 bg-teal-950/50 border border-teal-800/50 rounded-lg px-3 py-2">
                {autoLog}
              </div>
            )}

            {/* Auto-Search Candidates Picker */}
            {(wakuCandidates.length > 0 || jikanCandidates.length > 0) && (
              <div className="space-y-2 pt-1 max-h-48 overflow-y-auto pr-1">
                {wakuCandidates.map((cand) => {
                  const pLabel = cand.platform === 'bilibili' ? 'Bilibili' : 'Anime-Waku';
                  const pBg = cand.platform === 'bilibili' ? 'bg-sky-600' : 'bg-purple-600';
                  return (
                    <div
                      key={cand.url}
                      onClick={() => handleApplyWakuCandidate(cand)}
                      className="cursor-pointer bg-slate-900 hover:bg-purple-950/40 border border-slate-700 hover:border-purple-500/60 rounded-xl p-2 flex items-center gap-2.5 transition-all"
                    >
                      <img
                        src={formatImageUrl(cand.imageUrl)}
                        alt={cand.displayTitle}
                        className="w-10 h-14 object-cover rounded-lg shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`px-1.5 py-0.2 ${pBg} text-white text-[9px] font-bold rounded`}>
                            {pLabel}
                          </span>
                          <span className="text-[10px] text-amber-300 font-bold">
                            {cand.status === 'จบแล้ว'
                              ? `${cand.latestEp || cand.totalEp || ''} ตอนจบ`
                              : cand.totalEp && cand.totalEp > cand.latestEp
                              ? `มี ${cand.latestEp}/${cand.totalEp} ตอน`
                              : cand.latestEp > 0
                              ? `มี ${cand.latestEp} ตอน`
                              : 'รอฉาย'} • {cand.audioType}
                          </span>
                          {cand.vipBadge && (
                            <span className="text-[9px] text-amber-200 font-extrabold">
                              👑 {cand.vipBadge}
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-bold text-white truncate">
                          {cand.romajiTitle}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate">
                          {cand.thaiTitle}
                        </div>
                      </div>
                      <span className="px-2.5 py-1 bg-purple-600/30 text-purple-200 rounded-lg text-[11px] font-bold shrink-0">
                        ใช้ข้อมูลนี้
                      </span>
                    </div>
                  );
                })}

                {jikanCandidates.map((item) => (
                  <div
                    key={item.mal_id}
                    onClick={() => handleApplyJikanCandidate(item)}
                    className="cursor-pointer bg-slate-900 hover:bg-sky-950/40 border border-slate-700 hover:border-sky-500/60 rounded-xl p-2 flex items-center gap-2.5 transition-all"
                  >
                    <img
                      src={
                        item.images?.webp?.large_image_url ||
                        item.images?.jpg?.large_image_url ||
                        ''
                      }
                      alt={item.title}
                      className="w-10 h-14 object-cover rounded-lg shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="px-1.5 py-0.2 bg-sky-600 text-white text-[9px] font-bold rounded">
                          MyAnimeList
                        </span>
                        <span className="text-[10px] text-sky-300 font-bold">
                          {item.season?.toUpperCase() || ''} {item.year || ''}
                          {item.episodes ? ` • มี ${item.episodes} ตอน` : ''} • ฉาย:{' '}
                          {item.broadcast?.day || 'ไม่ระบุ'} {item.broadcast?.time || ''}
                        </span>
                      </div>
                      <div className="text-xs font-bold text-white truncate">
                        {item.title}
                      </div>
                    </div>
                    <span className="px-2.5 py-1 bg-sky-600/30 text-sky-200 rounded-lg text-[11px] font-bold shrink-0">
                      ดึงวันฉาย/ปก
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 2. Season (Winter, Spring, Summer, Fall) & Year */}
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-slate-300 mb-1.5">
              ช่วงฤดูกาลอนิเมะ (Seasonal Anime)
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(['WINTER', 'SPRING', 'SUMMER', 'FALL'] as AnimeSeason[]).map((sKey) => {
                const cfg = SEASON_CONFIGS[sKey];
                const isSel = season === sKey;
                return (
                  <button
                    key={sKey}
                    type="button"
                    onClick={() => setSeason(sKey)}
                    className={`p-2 rounded-xl border text-left transition-all ${
                      isSel
                        ? `bg-gradient-to-br ${cfg.gradient} text-white border-white/40 ring-2 ring-white/30 shadow`
                        : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="text-xs font-bold flex items-center justify-between">
                      <span>
                        {cfg.icon} {cfg.nameTh}
                      </span>
                    </div>
                    <div
                      className={`text-[10px] mt-0.5 ${
                        isSel ? 'text-white/90' : 'text-slate-500'
                      }`}
                    >
                      {cfg.nameEn.split(' ')[0]} ({cfg.startMonthTh})
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3. Tier Grade */}
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-slate-300 mb-1.5">
              ระดับความชอบ (Tier Ranking)
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
              {(['S', 'A+', 'A', 'B', 'C', 'EX'] as TierGrade[]).map((t) => {
                const cfg = TIER_CONFIGS[t];
                const isSelected = grade === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setGrade(t)}
                    className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all ${
                      isSelected
                        ? `bg-gradient-to-r ${cfg.gradient} text-white border-white/40 shadow ring-1 ring-white/30`
                        : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    {t} Tier
                  </button>
                );
              })}
            </div>
          </div>

          {/* 4. Broadcast Schedule & LINE Notification */}
          <div className="bg-slate-950/60 p-3.5 rounded-xl border border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs sm:text-sm font-bold text-rose-300 flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span>ตารางวันออกอากาศ & แจ้งเตือน LINE</span>
              </label>

              <button
                type="button"
                onClick={() => setNotifyLine(!notifyLine)}
                className={`px-3 py-1 rounded-lg text-xs font-bold border flex items-center gap-1.5 transition-all ${
                  notifyLine
                    ? 'bg-[#06C755]/20 text-[#4ef08f] border-[#06C755]/50'
                    : 'bg-slate-900 text-slate-400 border-slate-700'
                }`}
              >
                <Bell className="w-3.5 h-3.5" />
                <span>
                  {notifyLine ? '🔔 เปิดแจ้งเตือน LINE' : '🔕 ปิดแจ้งเตือน LINE'}
                </span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-400 mb-1">
                  วันออกอากาศ (หากลงในเว็บแล้วไม่รู้วันแน่ชัด ให้เลือก "ยังไม่รู้วันแน่ชัด")
                </label>
                <select
                  value={broadcastDay}
                  onChange={(e) => setBroadcastDay(e.target.value as BroadcastDay)}
                  aria-label="วันออกอากาศ"
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-rose-500"
                >
                  {BROADCAST_DAYS.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.id === 'UNKNOWN'
                        ? '🕵️ ยังไม่รู้วันแน่ชัด (ให้ระบบเช็คตอนใหม่จากเว็บอัตโนมัติ)'
                        : `📅 ${d.nameTh} (${d.nameEn})`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>เวลาฉาย (ถ้าทราบ)</span>
                </label>
                <input
                  type="time"
                  value={broadcastTime}
                  onChange={(e) => setBroadcastTime(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-rose-500"
                />
              </div>
            </div>
          </div>

          {/* 5. Episode Progress, Audio, Status, Year */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                ตอนที่ดูแล้ว
              </label>
              <input
                type="number"
                min="0"
                value={watchedEp}
                onChange={(e) => setWatchedEp(parseInt(e.target.value, 10) || 0)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-2.5 text-sm text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                ตอนล่าสุดที่ออกแล้ว
              </label>
              <input
                type="number"
                min="0"
                value={latestEp}
                onChange={(e) => setLatestEp(parseInt(e.target.value, 10) || 0)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-2.5 text-sm text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                จำนวนตอนทั้งหมด (ถ้าทราบ)
              </label>
              <input
                type="number"
                min="0"
                value={totalEp}
                onChange={(e) => setTotalEp(parseInt(e.target.value, 10) || 0)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-2.5 text-sm text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                รูปแบบเสียง/ซับ
              </label>
              <select
                value={audioType}
                onChange={(e) => setAudioType(e.target.value as any)}
                aria-label="รูปแบบเสียงและซับ"
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-2.5 text-sm text-white"
              >
                <option value="ซับไทย">ซับไทย</option>
                <option value="พากย์ไทย">พากย์ไทย</option>
                <option value="ซับ+พากย์">ซับไทย + พากย์ไทย</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                สถานะอนิเมะ
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                aria-label="สถานะอนิเมะ"
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-2.5 text-sm text-white"
              >
                <option value="กำลังฉาย">กำลังฉาย</option>
                <option value="จบแล้ว">จบแล้ว</option>
                <option value="รอฉาย">รอฉาย</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                ปีที่ออกอากาศ (ค.ศ.)
              </label>
              <input
                type="number"
                min="1990"
                max="2035"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10) || 2026)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-2.5 text-sm text-white"
              />
            </div>
          </div>

          {/* 6. Watch Sources (YouTube, Anime-Waku, etc.) */}
          <div className="border-t border-slate-800 pt-3 space-y-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-xs sm:text-sm font-bold text-teal-300 flex items-center gap-1.5">
                <LinkIcon className="w-4 h-4" />
                <span>ช่องทางรับชม (YouTube / Anime-Waku / เว็บอื่นๆ)</span>
              </label>

              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() =>
                    handleAddSource(
                      'YouTube (Muse Thailand)',
                      'https://www.youtube.com/@MuseThailand'
                    )
                  }
                  className="px-2 py-1 bg-red-600/20 hover:bg-red-600/35 text-red-200 rounded-lg text-[11px] border border-red-500/30"
                >
                  + YouTube (Muse)
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleAddSource(
                      'YouTube (Ani-One)',
                      'https://www.youtube.com/@AniOneThailand'
                    )
                  }
                  className="px-2 py-1 bg-red-600/20 hover:bg-red-600/35 text-red-200 rounded-lg text-[11px] border border-red-500/30"
                >
                  + YouTube (Ani-One)
                </button>
                <button
                  type="button"
                  onClick={() => handleAddSource('Bilibili', 'https://www.bilibili.tv/th/')}
                  className="px-2 py-1 bg-sky-600/20 hover:bg-sky-600/35 text-sky-200 rounded-lg text-[11px] border border-sky-500/30"
                >
                  + Bilibili
                </button>
                <button
                  type="button"
                  onClick={() => handleAddSource('Anime-Waku', 'https://anime-waku.com/')}
                  className="px-2 py-1 bg-purple-600/20 hover:bg-purple-600/35 text-purple-200 rounded-lg text-[11px] border border-purple-500/30"
                >
                  + Anime-Waku
                </button>
                <button
                  type="button"
                  onClick={() => handleAddSource()}
                  className="px-2 py-1 bg-teal-600/25 hover:bg-teal-600/40 text-teal-200 rounded-lg text-[11px] border border-teal-500/30 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  <span>เพิ่มลิงก์</span>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {sources.map((src, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={src.name}
                    onChange={(e) => handleSourceChange(idx, 'name', e.target.value)}
                    placeholder="ชื่อช่องทาง เช่น YouTube / Anime-Waku"
                    className="w-1/3 bg-slate-950 border border-slate-700 rounded-xl p-2 text-xs text-white"
                  />
                  <input
                    type="url"
                    value={src.url}
                    onChange={(e) => handleSourceChange(idx, 'url', e.target.value)}
                    placeholder="https://anime-waku.com/anime/... หรือลิงก์ YouTube"
                    className="flex-1 bg-slate-950 border border-slate-700 rounded-xl p-2 text-xs text-white"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveSource(idx)}
                    className="p-2 text-rose-400 hover:bg-rose-500/10 rounded-xl"
                    title="ลบลิงก์"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 7. Cover Image */}
          <div className="border-t border-slate-800 pt-3">
            <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-2 flex items-center gap-1.5">
              <ImageIcon className="w-4 h-4 text-purple-400" />
              <span>รูปภาพหน้าปกอนิเมะ</span>
            </label>

            <div className="flex gap-3 items-start">
              {imageUrl && (
                <div className="w-16 h-22 rounded-xl overflow-hidden bg-slate-950 border border-slate-700 shrink-0 aspect-[3/4]">
                  <img
                    src={formatImageUrl(imageUrl)}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="วาง URL รูปภาพหน้าปก หรือกดค้นหาอัตโนมัติด้านบน"
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl p-2 text-xs sm:text-sm text-white"
                />
                <label className="inline-flex cursor-pointer px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-medium border border-slate-700 items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" />
                  <span>อัปโหลดรูปภาพจากเครื่อง</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="bg-slate-900 px-5 py-3 border-t border-slate-800 flex items-center justify-between gap-2 shrink-0">
          {initialData && onDelete ? (
            <button
              type="button"
              onClick={() => {
                if (confirm(`ต้องการลบ "${title}" ออกจากรายการใช่หรือไม่?`)) {
                  onDelete(initialData.id);
                  onClose();
                }
              }}
              className="px-3 py-2 bg-rose-600/20 hover:bg-rose-600/35 text-rose-300 rounded-xl text-xs font-semibold border border-rose-500/30 flex items-center gap-1.5"
            >
              <Trash2 className="w-4 h-4" />
              <span>ลบเรื่องนี้</span>
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs sm:text-sm font-medium"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              form="anime-edit-form"
              className="px-5 py-2 bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-500 hover:to-indigo-500 text-white rounded-xl text-xs sm:text-sm font-bold shadow-lg"
            >
              บันทึกอนิเมะ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
